<template>
  <section class="quality-page">
    <!-- ===== HERO ===== -->
    <header class="hero">
      <h1 class="hero-title">Indikator Mutu & Keselamatan Pasien</h1>
      <p class="hero-sub">
        Kami mempublikasikan indikator kinerja utama sebagai wujud komitmen <strong>RSKJP Melinda CVC</strong> terhadap <strong>transparansi</strong>, <strong>keselamatan pasien</strong>, dan <strong>perbaikan berkelanjutan</strong>.
      </p>
    </header>

    <!-- ===== FILTER BAR ===== -->
    <div class="filter-bar">
      <!-- Desktop buttons -->
      <div class="filter-buttons desktop-only">
        <button
          v-for="cat in categories"
          :key="cat"
          :class="['filter-btn', {
            active: cat === selectedCat } ]"
          @click="selectedCat = cat"
        >
          {{
          cat }}
        </button>
      </div>

      <!-- Mobile select -->
      <select v-model="selectedCat" class="filter-select mobile-only">
        <option v-for="cat in categories" :key="cat" :value="cat">{{
            cat }}</option>
      </select>
    </div>

    <!-- ===== INDICATOR GRID ===== -->
    <div class="indicator-grid">
      <article
        v-for="ind in filteredIndicators"
        :key="ind.id"
        class="indicator-card"
        :class="ind.status"
      >
        <div class="card-head">
          <h2 class="ind-name">{{
            ind.name }}</h2>
          <span class="ind-category">{{
          ind.category }}</span>
        </div>
        <div class="card-body">
          <p class="ind-value">{{
            ind.value }} <small v-if="ind.unit">{{
            ind.unit }}</small></p>
          <p class="ind-target">Target: {{
          ind.target }}</p>
        </div>
        <footer class="card-foot">
          <span class="status-dot" :title="statusText(ind.status)"></span>
          <span class="status-label">{{
          statusText(ind.status) }}</span>
        </footer>
      </article>
    </div>
  </section>
</template>

<script setup>
import {
    ref, computed, watchEffect } from 'vue'

/* === Data Contoh === */
const indicators = ref([
  {
    id: 1, 
    name: 'Kepatuhan Cuci Tangan', 
    value: '94%', 
    target: '≥ 90%', 
    unit: '', 
    category: 'Keselamatan Pasien', 
    status: 'good' 
  },
  {
    id: 2, 
    name: 'Infeksi Luka Operasi', 
    value: '0,8%', 
    target: '< 1%', 
    unit: '', 
    category: 'Hasil Klinis', 
    status: 'good' 
  },
  {
    id: 3, 
    name: 'Readmisi 30 Hari', 
    value: '4%', 
    target: '< 5%', 
    unit: '', 
    category: 'Hasil Klinis', 
    status: 'good' 
  },
  {
    id: 4, 
    name: 'Waktu Tunggu IGD', 
    value: '42', 
    target: '< 60', 
    unit: 'menit', 
    category: 'Pengalaman Pasien', 
    status: 'good' 
  },
  {
    id: 5, 
    name: 'Bed Occupancy Rate', 
    value: '87%', 
    target: '60 - 85%', 
    unit: '', 
    category: 'Operasional', 
    status: 'caution' 
  },
  {
    id: 6, 
    name: 'Medication Error Rate', 
    value: '0,35%', 
    target: '< 0,3%', 
    unit: '', 
    category: 'Keselamatan Pasien', 
    status: 'alert' 
  },
  {
    id: 7, 
    name: 'Kepuasan Pasien', 
    value: '92%', 
    target: '≥ 90%', 
    unit: '', 
    category: 'Pengalaman Pasien', 
    status: 'good' 
  },
])

/* === Daftar Kategori === */
const categories = computed(() => {
  const set = new Set(indicators.value.map(i => i.category))
  return ['Semua', 
  ...Array.from(set)]
})

/* === Kategori Terpilih === */
const selectedCat = ref('Semua')
watchEffect(() => {
  if (!categories.value.includes(selectedCat.value)) selectedCat.value = 'Semua'
})

/* === Filter === */
const filteredIndicators = computed(() => {
  return selectedCat.value === 'Semua'
    ? indicators.value
    : indicators.value.filter(i => i.category === selectedCat.value)
})

/* === Status Label === */
function statusText(s){
  return s === 'good' ? 'Sesuai Target' : s === 'caution' ? 'Perlu Perhatian' : 'Kritis'
}
</script>

<style scoped>
/***** Base Layout *****/
.quality-page {
  /* background: var(--white); */
  color: var(--white);
  padding: 2.5rem 10%;
}
@media (max-width: 1500px){
    .quality-page{
        padding:2.5rem 5%;
    }
}

/***** Hero *****/
.hero-title {
    font-family: poppins;
    font-size: 2rem;
    margin: 0 0 1rem;
}
.hero-sub {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 800px;
}

/***** Filter Bar *****/
.filter-bar {
    margin: 2.5rem 0 1.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
}
.filter-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}
.filter-btn {
    padding: 0.5rem 1rem;
    background: var(--white);
    border: 1px solid var(--softgrey);
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s;
}
.filter-btn.active, .filter-btn:hover {
    background: var(--primary);
    color: var(--white);
}
.filter-select {
    width: 100%;
    padding: 0.6rem 1rem;
    border: 1px solid var(--softgrey);
    border-radius: 6px;
    font-size: 1rem;
}

/***** Grid *****/
.indicator-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1.25rem;
}
.indicator-card {
    border: 1px solid var(--softgrey);
    border-radius: 10px;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    background: var(--cream-bg);
    color: var(--navy);
    transition: transform 0.2s, box-shadow 0.2s;
}
.indicator-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 18px rgba(0,0,0,0.08);
}
.indicator-card.good   {
    border-left: 6px solid #28a745;
}
.indicator-card.caution{
    border-left: 6px solid #ffc107;
}
.indicator-card.alert  {
    border-left: 6px solid #dc3545;
}

.card-head {
    display:flex;
    flex-direction:column;
    gap:0.25rem;
}
.ind-name {
    font-size:1.125rem;
    margin:0;
    font-weight:700;
}
.ind-category {
    font-size:0.875rem;
    color:var(--grey);
}
.card-body {
    display:flex;
    flex-direction:column;
    gap:0.25rem;
}
.ind-value {
    font-size:1.75rem;
    margin:0;
    font-weight:700;
}
.ind-value small{
    font-size:0.75rem;
    margin-left:2px;
}
.ind-target {
    font-size:0.875rem;
    color:var(--grey-dark, #4a4a4a);
}
.card-foot {
    margin-top:auto;
    display:flex;
    align-items:center;
    gap:0.5rem;
}
.status-dot {
    width:12px;
    height:12px;
    border-radius:50%;
    background: currentColor;
}
.indicator-card.good .status-dot   {
    color:#28a745;
}
.indicator-card.caution .status-dot{
    color:#ffc107;
}
.indicator-card.alert .status-dot  {
    color:#dc3545;
}
.status-label {
    font-size:0.875rem;
}

/***** Responsive Helpers *****/
.mobile-only {
    display:none;
}
@media(max-width:750px){
    .quality-page{
        padding:2.5rem 2.5%;
    }
    .hero-title{
        font-size: 1.2rem;
    }
    .desktop-only{
        display:none;
    } 
    .mobile-only{
        display:block;
    } 
}
</style>
